import React, { useState } from "react";
import { matchPhysicians } from "../api/physicians.js";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";

const SUBSPECIALTY_LABELS = {
  // Oncology
  "hematology": "Hematology",
  "solid-breast-gyn": "Breast/GYN",
  "solid-thoracic-gi": "Thoracic/GI",
  "solid-gu": "GU/Prostate",
  "medical-general": "General Med Onc",
  "medical-integrative": "Integrative",
  // Orthopedic
  "joint-replacement-hip-knee": "Joint Replacement (Hip/Knee)",
  "joint-replacement-shoulder": "Joint Replacement (Shoulder)",
  "sports-medicine-operative": "Sports Med (Operative)",
  "sports-medicine-non-op": "Sports Med (Non-Op)",
  "hand-upper-extremity": "Hand & Upper Extremity",
  "foot-ankle": "Foot & Ankle",
  "spine": "Spine",
  "trauma-fracture-care": "Trauma/Fracture",
  "pediatric-orthopedics": "Pediatric Ortho",
  "physical-medicine-rehab": "PM&R",
  "pain-management": "Pain Management",
  "general-orthopedics": "General Ortho",
};

const FACTOR_LABELS = {
  // Oncology
  subspecialty: "Subspecialty",
  condition: "Conditions",
  location: "Location",
  acceptingNew: "Accepting",
  insurance: "Insurance",
  ageRange: "Age",
  language: "Language",
  // Orthopedic only
  bodyPart: "Body Part",
  operativeFit: "Operative Fit",
};

const BODY_PART_OPTIONS = [
  { value: "", label: "— Any —" },
  { value: "knee", label: "Knee" },
  { value: "hip", label: "Hip" },
  { value: "shoulder", label: "Shoulder" },
  { value: "elbow", label: "Elbow" },
  { value: "wrist-hand", label: "Wrist / Hand" },
  { value: "ankle-foot", label: "Ankle / Foot" },
  { value: "spine-cervical", label: "Spine — Cervical" },
  { value: "spine-lumbar", label: "Spine — Lumbar" },
];

export default function PhysicianMatch({ navigate }) {
  const { currentSubTenant: currentTenant } = useHierarchy();
  const isOrtho = currentTenant?.specialty === "orthopedics";

  const [form, setForm] = useState({
    referralReason: "",
    patientCondition: "",
    patientAge: "",
    patientCity: "",
    patientState: "",
    patientZip: "",
    insurance: "",
    language: "",
    urgency: "standard",
    // Orthopedic triage
    bodyPart: "",
    side: "",
    injuryRelated: false,
    priorSurgery: false,
    diagnosedOA: false,
    toldNeedsReplacement: false,
    wantsNonOp: false,
    hasImaging: false,
    secondOpinion: false,
    useLLMRerank: false,
    limit: 5,
  });
  const [matching, setMatching] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleMatch = async () => {
    if (!form.referralReason.trim()) {
      alert("Please enter a referral reason");
      return;
    }
    setMatching(true);
    setResult(null);
    try {
      const res = await matchPhysicians(form);
      setResult(res);
    } catch (err) {
      alert("Match failed: " + err.message);
    } finally {
      setMatching(false);
    }
  };

  return (
    <div>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate("physicians")}>← Back</button>
        <h1 style={s.title}>Physician Match</h1>
      </div>

      <div style={s.body}>
        {/* ─── Left: Input form ─────────── */}
        <div style={s.leftPane}>
          <div style={s.sectionTitle}>Simulate a Referral</div>

          <Field label="Referral Reason *">
            <textarea
              style={s.textarea}
              value={form.referralReason}
              onChange={(e) => setField("referralReason", e.target.value)}
              rows={3}
              placeholder="e.g., 52-year-old woman with newly diagnosed invasive ductal carcinoma of the right breast, stage II. Referring for oncology evaluation and treatment planning."
            />
          </Field>

          <Field label="Patient Condition">
            <input
              style={s.input}
              value={form.patientCondition}
              onChange={(e) => setField("patientCondition", e.target.value)}
              placeholder="Breast Cancer Stage II"
            />
          </Field>

          <div style={s.row}>
            <Field label="Age" flex={1}>
              <input
                type="number"
                style={s.input}
                value={form.patientAge}
                onChange={(e) => setField("patientAge", e.target.value)}
                placeholder="52"
              />
            </Field>
            <Field label="Urgency" flex={1}>
              <select style={s.input} value={form.urgency} onChange={(e) => setField("urgency", e.target.value)}>
                <option value="standard">Standard</option>
                <option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>

          <div style={s.row}>
            <Field label="City" flex={2}>
              <input
                style={s.input}
                value={form.patientCity}
                onChange={(e) => setField("patientCity", e.target.value)}
                placeholder="Riverside"
              />
            </Field>
            <Field label="State" flex={1}>
              <input
                style={s.input}
                value={form.patientState}
                onChange={(e) => setField("patientState", e.target.value.toUpperCase())}
                placeholder="CA"
                maxLength={2}
              />
            </Field>
            <Field label="Zip" flex={1}>
              <input
                style={s.input}
                value={form.patientZip}
                onChange={(e) => setField("patientZip", e.target.value)}
                placeholder="92501"
              />
            </Field>
          </div>

          {isOrtho && (
            <>
              <div style={s.triageHeader}>Orthopedic Triage</div>
              <div style={s.row}>
                <Field label="Body Part" flex={2}>
                  <select style={s.input} value={form.bodyPart} onChange={(e) => setField("bodyPart", e.target.value)}>
                    {BODY_PART_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Side" flex={1}>
                  <select style={s.input} value={form.side} onChange={(e) => setField("side", e.target.value)}>
                    <option value="">—</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="bilateral">Bilateral</option>
                  </select>
                </Field>
              </div>
              <div style={s.triageCheckGrid}>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.injuryRelated} onChange={(e) => setField("injuryRelated", e.target.checked)} />
                  <span>Injury / trauma related</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.priorSurgery} onChange={(e) => setField("priorSurgery", e.target.checked)} />
                  <span>Prior surgery on this area</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.diagnosedOA} onChange={(e) => setField("diagnosedOA", e.target.checked)} />
                  <span>Diagnosed with osteoarthritis</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.toldNeedsReplacement} onChange={(e) => setField("toldNeedsReplacement", e.target.checked)} />
                  <span>Told they need a replacement</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.wantsNonOp} onChange={(e) => setField("wantsNonOp", e.target.checked)} />
                  <span>Wants to avoid surgery</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.hasImaging} onChange={(e) => setField("hasImaging", e.target.checked)} />
                  <span>Has imaging (MRI/X-ray)</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={form.secondOpinion} onChange={(e) => setField("secondOpinion", e.target.checked)} />
                  <span>Second opinion</span>
                </label>
              </div>
            </>
          )}

          <div style={s.row}>
            <Field label="Insurance" flex={1}>
              <input
                style={s.input}
                value={form.insurance}
                onChange={(e) => setField("insurance", e.target.value)}
                placeholder={isOrtho ? "Aetna" : "BCBS"}
              />
            </Field>
            <Field label="Language" flex={1}>
              <input
                style={s.input}
                value={form.language}
                onChange={(e) => setField("language", e.target.value)}
                placeholder="English"
              />
            </Field>
          </div>

          <Field label="">
            <label style={s.toggle}>
              <input
                type="checkbox"
                checked={form.useLLMRerank}
                onChange={(e) => setField("useLLMRerank", e.target.checked)}
              />
              <span>Use AI re-ranking (uses free-text fields like bio & clinical interests)</span>
            </label>
          </Field>

          <button style={s.matchBtn} onClick={handleMatch} disabled={matching}>
            {matching ? "Matching..." : "Match Physicians"}
          </button>
        </div>

        {/* ─── Right: Results ───────────── */}
        <div style={s.rightPane}>
          <div style={s.sectionTitle}>
            Ranked Matches
            {result && (
              <span style={s.countBadge}>
                {result.matches.length} of {result.totalInCatalog} physicians
              </span>
            )}
          </div>

          {!result && !matching && (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>🎯</div>
              <div>Fill in the referral details and click "Match Physicians"</div>
              <div style={s.emptyHint}>
                {isOrtho
                  ? "The orthopedic match engine ranks providers by body part expertise, triage-derived subspecialty (injury vs OA vs replacement), operative philosophy, nearest office, insurance, and age fit. Optionally use AI re-ranking to leverage bio and clinical interests."
                  : "The match engine ranks physicians by subspecialty fit, conditions treated, location proximity, insurance, age range, and language. Optionally use AI re-ranking to leverage bio and clinical interests."}
              </div>
            </div>
          )}

          {matching && (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>⏳</div>
              <div>Scoring physicians...</div>
            </div>
          )}

          {result && result.matches.length === 0 && (
            <div style={s.emptyState}>
              <div style={s.emptyIcon}>😕</div>
              <div>No matching physicians found</div>
            </div>
          )}

          {result && result.matches.map((match) => (
            <MatchCard key={match.physician.id} match={match} />
          ))}
        </div>
      </div>
    </div>
  );
}

function MatchCard({ match }) {
  const [expanded, setExpanded] = useState(false);
  const { physician, score, factorBreakdown, reasoning, rank, source } = match;

  const scoreColor = score >= 70 ? "#059669" : score >= 40 ? "#f59e0b" : "#dc2626";

  return (
    <div style={s.card}>
      <div style={s.cardHeader} onClick={() => setExpanded(!expanded)}>
        <div style={{ ...s.rankBadge, backgroundColor: scoreColor }}>{rank}</div>
        <div style={s.cardMain}>
          <div style={s.cardName}>{physician.name}</div>
          <div style={s.cardSub}>
            <div style={s.subBadgeWrap}>
              {(Array.isArray(physician.subspecialties) && physician.subspecialties.length > 0
                ? physician.subspecialties
                : [physician.subspecialty].filter(Boolean)
              ).map((sub) => (
                <span key={sub} style={s.subBadge}>
                  {SUBSPECIALTY_LABELS[sub] || sub}
                </span>
              ))}
            </div>
            {Array.isArray(physician.offices) && physician.offices.length > 0 ? (
              <span style={s.locText}>
                {physician.offices.slice(0, 2).map((o) => `${o.name || o.city}${o.city && o.name ? ` (${o.city})` : ""}`).join(", ")}
                {physician.offices.length > 2 ? ` +${physician.offices.length - 2} more` : ""}
              </span>
            ) : physician.location?.city ? (
              <span style={s.locText}>
                {physician.location.name || ""} — {physician.location.city}, {physician.location.state}
              </span>
            ) : null}
          </div>
        </div>
        <div style={s.scoreBlock}>
          <div style={{ ...s.scoreValue, color: scoreColor }}>{score}</div>
          <div style={s.scoreMax}>/ 100</div>
        </div>
      </div>

      <div style={s.reasoning}>
        {source === "llm-reranked" && <span style={s.aiTag}>AI</span>}
        {reasoning}
      </div>

      {expanded && factorBreakdown && (
        <div style={s.breakdown}>
          <div style={s.breakdownTitle}>Factor Breakdown</div>
          {Object.entries(factorBreakdown).map(([key, factor]) => (
            <div key={key} style={s.factorRow}>
              <div style={s.factorLabel}>{FACTOR_LABELS[key] || key}</div>
              <div style={s.factorBarWrap}>
                <div
                  style={{
                    ...s.factorBar,
                    width: `${(factor.score / factor.max) * 100}%`,
                    background: factor.score === factor.max ? "#059669" : factor.score > 0 ? "#60a5fa" : "#e5e5e5",
                  }}
                />
              </div>
              <div style={s.factorScore}>{factor.score}/{factor.max}</div>
            </div>
          ))}
          <div style={s.factorDetails}>
            {Object.entries(factorBreakdown).map(([key, factor]) => (
              factor.detail && (
                <div key={key} style={s.factorDetail}>
                  <strong>{FACTOR_LABELS[key] || key}:</strong> {factor.detail}
                </div>
              )
            ))}
          </div>
        </div>
      )}

      <button style={s.expandBtn} onClick={() => setExpanded(!expanded)}>
        {expanded ? "Hide details" : "Show factor breakdown"}
      </button>
    </div>
  );
}

function Field({ label, flex, children }) {
  return (
    <div style={{ ...s.field, ...(flex ? { flex } : {}) }}>
      {label && <label style={s.label}>{label}</label>}
      {children}
    </div>
  );
}

const s = {
  header: {
    display: "flex", alignItems: "center", gap: 16,
    padding: "20px 32px", borderBottom: "1px solid #e5e5e5",
  },
  backBtn: {
    padding: "6px 12px", background: "#fff", color: "#0f0f0f",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  body: { display: "grid", gridTemplateColumns: "400px 1fr", gap: 24, padding: "24px 32px" },
  leftPane: { borderRight: "1px solid #f0f0f0", paddingRight: 24 },
  rightPane: {},
  sectionTitle: {
    fontSize: 14, fontWeight: 600, color: "#0f0f0f", marginBottom: 16,
    textTransform: "uppercase", letterSpacing: "0.03em",
    display: "flex", alignItems: "center", gap: 8,
  },
  countBadge: {
    fontSize: 11, color: "#999", background: "#f5f5f5", padding: "2px 8px",
    borderRadius: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0,
  },
  row: { display: "flex", gap: 12, alignItems: "flex-end" },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, fontWeight: 500, color: "#666", marginBottom: 6 },
  input: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6,
    fontSize: 13, outline: "none", color: "#0f0f0f", background: "#fff",
    boxSizing: "border-box", fontFamily: "inherit",
  },
  textarea: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6,
    fontSize: 13, outline: "none", color: "#0f0f0f", background: "#fff",
    boxSizing: "border-box", fontFamily: "inherit", resize: "vertical",
  },
  toggle: {
    display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#555",
    cursor: "pointer", lineHeight: 1.4,
  },
  triageHeader: {
    fontSize: 11, fontWeight: 600, color: "#1d4ed8", marginTop: 12, marginBottom: 8,
    textTransform: "uppercase", letterSpacing: "0.05em",
    padding: "6px 10px", background: "#eef4ff", borderRadius: 6,
    display: "inline-block",
  },
  triageCheckGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
    padding: "8px 0 14px", marginBottom: 4,
  },
  matchBtn: {
    width: "100%", padding: "11px", background: "#0f0f0f", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
    marginTop: 8,
  },
  emptyState: {
    padding: "60px 20px", textAlign: "center", color: "#999", fontSize: 13,
    border: "1px dashed #e5e5e5", borderRadius: 10,
  },
  emptyIcon: { fontSize: 32, marginBottom: 12 },
  emptyHint: {
    fontSize: 12, color: "#aaa", marginTop: 12, maxWidth: 420,
    marginLeft: "auto", marginRight: "auto", lineHeight: 1.5,
  },
  card: {
    border: "1px solid #e5e5e5", borderRadius: 10, padding: 16, marginBottom: 12,
  },
  cardHeader: { display: "flex", alignItems: "center", gap: 12, cursor: "pointer" },
  rankBadge: {
    width: 32, height: 32, borderRadius: 16, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 15, fontWeight: 700, flexShrink: 0,
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: { fontSize: 15, fontWeight: 600, color: "#0f0f0f" },
  cardSub: { display: "flex", gap: 10, alignItems: "center", marginTop: 4, flexWrap: "wrap" },
  subBadgeWrap: { display: "flex", flexWrap: "wrap", gap: 4 },
  subBadge: {
    display: "inline-block", padding: "2px 10px", borderRadius: 10,
    background: "#eef4ff", color: "#1d4ed8", fontSize: 11, fontWeight: 500,
  },
  locText: { fontSize: 12, color: "#999" },
  scoreBlock: { textAlign: "right" },
  scoreValue: { fontSize: 22, fontWeight: 700, lineHeight: 1 },
  scoreMax: { fontSize: 11, color: "#999", marginTop: 2 },
  reasoning: {
    marginTop: 12, padding: "10px 12px", background: "#fafafa", borderRadius: 6,
    fontSize: 12, color: "#444", lineHeight: 1.5,
  },
  aiTag: {
    display: "inline-block", padding: "1px 6px", borderRadius: 4,
    background: "#1d4ed8", color: "#fff", fontSize: 10, fontWeight: 600,
    marginRight: 6, verticalAlign: "middle",
  },
  expandBtn: {
    width: "100%", marginTop: 10, padding: 6, background: "transparent",
    border: "none", color: "#1d4ed8", fontSize: 12, fontWeight: 500, cursor: "pointer",
  },
  breakdown: {
    marginTop: 12, padding: "12px 14px", background: "#fafafa", borderRadius: 6,
  },
  breakdownTitle: {
    fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 10,
    textTransform: "uppercase", letterSpacing: "0.03em",
  },
  factorRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6 },
  factorLabel: { width: 85, fontSize: 11, color: "#666" },
  factorBarWrap: {
    flex: 1, height: 6, background: "#e5e5e5", borderRadius: 3, overflow: "hidden",
  },
  factorBar: { height: "100%", borderRadius: 3, transition: "width 0.3s" },
  factorScore: { width: 40, fontSize: 11, color: "#666", textAlign: "right", fontFamily: "monospace" },
  factorDetails: {
    marginTop: 12, paddingTop: 10, borderTop: "1px solid #e5e5e5",
  },
  factorDetail: { fontSize: 11, color: "#555", marginBottom: 4, lineHeight: 1.4 },
};
